import { ReactNode, useEffect, useRef, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import Iconify from "../../../minimals/components/iconify";
import { DocumentsOrderInfo, DocumentsOrderProperty } from "../types";

const statusOptions = [
  "In process",
  "Transferred",
  "Tentative",
  "Hold",
  "Pending",
  "Firm",
  "Completed",
  "Closed",
  "Adjourned",
  "Canceled",
  "Duplicate",
  "Dormant",
];

export const statusColorMap: Record<string, string> = {
  "In process": "#3b49d4",
  Transferred: "#3b49d4",
  Tentative: "#252f85",
  Hold: "#7e60ab",
  Pending: "#552899",
  Firm: "#47a842",
  Completed: "#2a8a25",
  Closed: "#1a5417",
  Adjourned: "#e0a800",
  Canceled: "#ff2929",
  Duplicate: "#ac0000",
  Dormant: "#78909c",
  Default: "#000000",
};

const formatShortDate = (value?: string) => {
  if (!value) {
    return "--";
  }

  return format(new Date(value), "M/d/yyyy");
};

const formatDateInputValue = (value?: string) => {
  if (!value) {
    return "";
  }

  return format(new Date(value), "yyyy-MM-dd");
};

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) {
    return "--";
  }

  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

const formatOrderAddress = (property?: DocumentsOrderProperty) => {
  if (!property) {
    return "";
  }

  return [
    property.address1,
    property.address2,
    [property.city, property.state].filter(Boolean).join(", "),
  ]
    .filter(Boolean)
    .join(", ");
};

export function OrderSummaryCard({
  orderNumber,
  orderInfo,
  relatedOrders,
  isLoading,
  onOrderInfoUpdated,
  compactLoading = false,
}: {
  orderNumber: string;
  orderInfo: DocumentsOrderInfo | null;
  relatedOrders: string[];
  isLoading: boolean;
  onOrderInfoUpdated: () => Promise<void>;
  compactLoading?: boolean;
}) {
  const [relatedOpen, setRelatedOpen] = useState(false);
  const [propertyOpen, setPropertyOpen] = useState(true);
  const [propertyIndex, setPropertyIndex] = useState(0);
  const properties = orderInfo?.properties ?? [];
  const property = properties[propertyIndex];
  const displayOrderNumber = orderInfo?.number || orderNumber;
  const titleNumber = orderInfo?.titleCode
    ? `${orderInfo.titleCode}-${displayOrderNumber}`
    : displayOrderNumber;
  // Subject/header line is order-level and must not change while paging through
  // properties, so it is based on the primary property, never the selected one.
  const orderDescriptor =
    orderInfo?.emailSubjectLine ||
    formatOrderAddress(properties[0]) ||
    orderInfo?.identifier;
  const orderLine = [titleNumber, orderDescriptor].filter(Boolean).join(" / ");
  const loanAmount = orderInfo?.loanAmounts?.length
    ? orderInfo.loanAmounts.map(formatCurrency).join(" & ")
    : "--";
  const hasLoanAmounts = Boolean(orderInfo?.loanAmounts?.length);
  const hasSalesPrice = Boolean(orderInfo) && orderInfo?.salesPrice !== 0;
  const isExchangeOrder = displayOrderNumber.toLowerCase().startsWith("madex");
  const hasExchangeData = Boolean(orderInfo?.exchangeData);
  const propertyType =
    orderInfo?.propertyType ?? orderInfo?.propertyUse ?? "--";
  const propertySearchParts = property
    ? [
        property.address1,
        property.address2,
        property.city,
        property.state,
        property.zip,
      ].filter(Boolean)
    : [];
  const propertySearchText = propertySearchParts.join(", ");
  const googleMapsLink = `https://www.google.com/maps/place/${encodeURIComponent(
    propertySearchText,
  )
    .replace(/%20/g, "+")
    .replace(/%2C/g, ",")}`;
  const googleSearchLink = `https://www.google.com/search?q=${encodeURIComponent(
    propertySearchText,
  )
    .replace(/%20/g, "+")
    .replace(/%2C/g, ",")}`;

  useEffect(() => {
    setPropertyIndex(0);
  }, [orderInfo?.number]);

  const selectProperty = (offset: number) => {
    setPropertyIndex((current) => {
      const next = current + offset;
      if (next < 0 || next >= properties.length) {
        return current;
      }

      return next;
    });
  };

  if (isLoading) {
    return compactLoading ? <CompactOrderSummarySkeleton /> : <OrderSummarySkeleton />;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1,
        borderColor: "#091A7A",
        overflow: "hidden",
        maxHeight: { lg: "calc(100vh - 280px)" },
        overflowY: { lg: "auto" },
        scrollbarGutter: { lg: "stable" },
        flexShrink: 0,
        boxShadow: "0 8px 24px rgba(22, 28, 36, 0.04)",
      }}
    >
      <Box sx={{ px: { xs: 1.5, md: 2 }, py: { xs: 1, md: 1.25 }, backgroundColor: "background.paper" }}>
        <Stack spacing={1}>
          {/* Below md the sheet header already shows the order number, so this would
              be a duplicate two lines apart. Desktop has no such header. */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <CopyableText value={displayOrderNumber}>
              <Typography
                variant="body1"
                sx={{
                  color: "primary.dark",
                  fontSize: 19,
                  fontWeight: 500,
                  lineHeight: 1.1,
                  wordBreak: "break-word",
                }}
              >
                {titleNumber}
              </Typography>
            </CopyableText>
          </Box>

          <CopyableText value={orderLine || displayOrderNumber}>
            <Typography
              variant="body1"
              sx={{
                color: "primary.dark",
                fontSize: 13,
                fontWeight: 400,
                lineHeight: 1.25,
                wordBreak: "break-word",
              }}
            >
              Order {orderLine || displayOrderNumber}
            </Typography>
          </CopyableText>

          {relatedOrders.length > 0 && (
            <Box>
              <Button size="small"
                variant="text"
                color="inherit"
                onClick={() => setRelatedOpen((current) => !current)}
                endIcon={
                  <Iconify
                    icon={
                      relatedOpen
                        ? "eva:chevron-up-fill"
                        : "eva:chevron-down-fill"
                    }
                    width={20}
                  />
                }
                sx={{
                  minHeight: 28,
                  px: 0,
                  color: "primary.dark",
                  fontWeight: 500,
                }}
              >
                Related Orders
              </Button>
              <Collapse in={relatedOpen}>
                <Stack spacing={0.5} sx={{ pt: 0.5 }}>
                  {relatedOrders.map((relatedOrder) => (
                    <Link
                      key={relatedOrder}
                      href={`/documents/${encodeURIComponent(relatedOrder.split(" ")[0])}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ color: "primary.dark", fontSize: 12 }}
                    >
                      {relatedOrder}
                    </Link>
                  ))}
                </Stack>
              </Collapse>
            </Box>
          )}
        </Stack>
      </Box>

      <Divider sx={{ mx: 2 }} />

      <Box
        sx={{
          px: { xs: 1.5, md: 2 },
          py: { xs: 1.25, md: 1.5 },
          backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.025),
        }}
      >
        <Box
          sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
        >
          <OrderSummaryMetric label="Property Type" value={propertyType} />
          <OrderSummaryMetric
            label={isExchangeOrder ? "Exchange Type" : "Transaction Type"}
            value={isExchangeOrder ? orderInfo?.productType || "--" : orderInfo?.transactionType || "--"}
          />
          {hasExchangeData && (
            <>
              <OrderSummaryMetric
                label="45 Day Deadline"
                value={formatShortDate(orderInfo?.exchangeData?.deadline45Day || undefined)}
              />
              <OrderSummaryMetric
                label="180 Day Deadline"
                value={formatShortDate(orderInfo?.exchangeData?.deadline180Day || undefined)}
              />
            </>
          )}
          <OrderSummaryMetric
            label="Commitment Date"
            value={formatShortDate(orderInfo?.commitmentDate)}
          />
          <OrderStatusMetric
            status={orderInfo?.status}
            settlementDate={orderInfo?.settlementDate}
            orderNumber={displayOrderNumber}
            isExchangeOrder={isExchangeOrder}
            onUpdated={onOrderInfoUpdated}
          />
          {hasSalesPrice && (
            <OrderSummaryMetric
              label="Purchase Price"
              value={formatCurrency(orderInfo?.salesPrice)}
              gridColumn={hasLoanAmounts ? undefined : "1 / -1"}
            />
          )}
          {hasLoanAmounts && (
            <OrderSummaryMetric
              label={orderInfo?.loanAmounts?.length === 1 ? "Loan Amount" : "Loan Amounts"}
              value={loanAmount}
              gridColumn={hasSalesPrice ? undefined : "1 / -1"}
            />
          )}
        </Box>
      </Box>

      {property && (
        <>
          <Divider sx={{ mx: 2 }} />
          <Box sx={{ px: { xs: 1.5, md: 2 }, py: { xs: 1, md: 1.25 } }}>
            <Stack spacing={1}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary", fontWeight: 400 }}
                >
                  Property Address
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setPropertyOpen((current) => !current)}
                >
                  <Iconify
                    icon={
                      propertyOpen
                        ? "eva:chevron-up-fill"
                        : "eva:chevron-down-fill"
                    }
                  />
                </IconButton>
              </Stack>

              <Collapse in={propertyOpen}>
                <Stack spacing={1.25}>
                  <ClickToCopy text={propertySearchText}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "primary.dark",
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "19px",
                      }}
                    >
                      {property.address1 || "--"}
                    </Typography>
                    {property.address2 && (
                      <Typography
                        variant="body1"
                        sx={{
                          color: "primary.dark",
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: "19px",
                        }}
                      >
                        {property.address2}
                      </Typography>
                    )}
                    <Typography
                      variant="body1"
                      sx={{
                        color: "primary.dark",
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "19px",
                      }}
                    >
                      {[property.city, property.state]
                        .filter(Boolean)
                        .join(", ")}{" "}
                      {property.zip || ""}
                    </Typography>
                  </ClickToCopy>

                  {isExchangeOrder && property.escrowBriefLegal && (
                    <Typography
                      variant="body2"
                      sx={{ color: "primary.dark", fontWeight: 500, lineHeight: 1.3 }}
                    >
                      {property.escrowBriefLegal}
                    </Typography>
                  )}

                  <PropertyDetails property={property} />

                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      {properties.length > 1 && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <IconButton
                            size="small"
                            onClick={() => selectProperty(-1)}
                            disabled={propertyIndex === 0}
                            sx={{ color: "primary.dark", width: 24, height: 24, p: 0 }}
                          >
                            <Iconify icon="eva:arrow-ios-back-fill" width={16} />
                          </IconButton>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 500,
                              fontSize: 12,
                              lineHeight: 1.3,
                              textAlign: "left",
                            }}
                          >
                            Property
                            <Box component="span" sx={{ display: "block" }}>
                              {propertyIndex + 1} of {properties.length}
                            </Box>
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => selectProperty(1)}
                            disabled={propertyIndex === properties.length - 1}
                            sx={{ color: "primary.dark", width: 24, height: 24, p: 0 }}
                          >
                            <Iconify icon="eva:arrow-ios-forward-fill" width={16} />
                          </IconButton>
                        </Stack>
                      )}
                    </Box>

                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {orderInfo?.identifier && (
                        <Tooltip title="Open in Select">
                          <IconButton
                            component="a"
                            href={`sp-select://./ordertracking/order?id=${orderInfo.identifier}`}
                            // Desktop only: the sp-select:// deep link opens a
                            // Windows desktop app, so it is dead weight on mobile.
                            sx={{ display: { xs: "none", md: "inline-flex" }, color: "#5f7faa", p: 0.75 }}
                          >
                            <Iconify
                              icon="material-symbols:home-work-outline"
                              width={26}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                      {propertySearchText && (
                        <>
                          <Tooltip title="Search in Google Maps">
                            <IconButton
                              component="a"
                              href={googleMapsLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ p: 0.75 }}
                            >
                              <Iconify icon="logos:google-maps" width={26} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Search in Google">
                            <IconButton
                              component="a"
                              href={googleSearchLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ p: 0.75 }}
                            >
                              <Iconify icon="logos:google-icon" width={26} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              </Collapse>
            </Stack>
          </Box>
        </>
      )}
    </Paper>
  );
}

function CompactOrderSummarySkeleton() {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1,
        borderColor: "#091A7A",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <Box sx={{ px: { xs: 1.5, md: 2 }, py: { xs: 1, md: 1.25 } }}>
        <Stack spacing={1}>
          <Skeleton variant="text" width="90%" height={30} />
          <Skeleton variant="text" width={120} height={28} />
        </Stack>
      </Box>
      <Divider sx={{ mx: 2 }} />
      <Box sx={{ px: { xs: 1.5, md: 2 }, py: { xs: 1.25, md: 1.5 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={48} />
        </Box>
      </Box>
      <Divider sx={{ mx: 2 }} />
      <Box sx={{ px: { xs: 1.5, md: 2 }, py: { xs: 1, md: 1.25 } }}>
        <Stack spacing={1}>
          <Skeleton variant="text" width={160} height={28} />
          <Skeleton variant="text" width="72%" height={28} />
          <Skeleton variant="text" width="56%" height={28} />
        </Stack>
      </Box>
    </Paper>
  );
}

function OrderSummarySkeleton() {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1,
        borderColor: "#091A7A",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <Box sx={{ p: 2 }}>
        <Stack spacing={1.25}>
          <Skeleton variant="text" width="80%" height={48} />
          <Skeleton variant="text" width="92%" height={30} />
          <Skeleton variant="text" width={130} height={32} />
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Skeleton variant="rounded" height={58} />
          <Skeleton variant="rounded" height={58} />
          <Skeleton variant="rounded" height={58} />
          <Skeleton variant="rounded" height={58} />
          <Skeleton
            variant="rounded"
            height={58}
            sx={{ gridColumn: "1 / -1" }}
          />
        </Box>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Skeleton variant="text" width={160} height={30} />
          <Skeleton variant="text" width="75%" height={34} />
          <Skeleton variant="text" width="55%" height={34} />
          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
          >
            <Skeleton variant="rounded" height={52} />
            <Skeleton variant="rounded" height={52} />
            <Skeleton variant="rounded" height={52} />
            <Skeleton variant="rounded" height={52} />
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}

function CopyableText({
  value,
  children,
}: {
  value: string;
  children: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number>();

  useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

  const handleClick = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => setCopied(false), 1000);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: "relative",
        minWidth: 0,
        cursor: "pointer",
        "&:hover .copy-overlay": { opacity: 1 },
      }}
    >
      {children}
      <Box
        className="copy-overlay"
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 0.75,
          backgroundColor: "rgba(22, 28, 36, 0.8)",
          color: "common.white",
          fontSize: 12,
          fontWeight: 500,
          opacity: copied ? 1 : 0,
          pointerEvents: "none",
          transition: (theme) => theme.transitions.create("opacity"),
        }}
      >
        {copied ? "Copied" : "Copy"}
      </Box>
    </Box>
  );
}

function ClickToCopy({ text, children }: { text: string; children: ReactNode }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number>();

  useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

  const handleClick = () => {
    navigator.clipboard?.writeText(text);
    setCopied(true);
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => setCopied(false), 1000);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: "relative",
        minWidth: 0,
        cursor: "pointer",
        "&:hover .copy-overlay": { opacity: 1 },
      }}
    >
      {children}
      <Box
        className="copy-overlay"
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 0.75,
          backgroundColor: "rgba(22, 28, 36, 0.8)",
          color: "common.white",
          fontSize: 12,
          fontWeight: 500,
          opacity: copied ? 1 : 0,
          pointerEvents: "none",
          transition: (theme) => theme.transitions.create("opacity"),
        }}
      >
        {copied ? "Copied" : "Copy"}
      </Box>
    </Box>
  );
}

function OrderSummaryMetric({
  label,
  value,
  gridColumn,
}: {
  label: string;
  value: string;
  gridColumn?: string;
}) {
  return (
    <Box sx={{ gridColumn, minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          fontSize: 11,
          fontWeight: 400,
          lineHeight: 1.2,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <ClickToCopy text={value}>
        <Typography
          variant="body1"
          sx={{
            color: "primary.dark",
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.15,
            wordBreak: "break-word",
          }}
        >
          {value}
        </Typography>
      </ClickToCopy>
    </Box>
  );
}

function OrderStatusMetric({
  status,
  settlementDate,
  orderNumber,
  isExchangeOrder,
  onUpdated,
}: {
  status?: string;
  settlementDate?: string;
  orderNumber: string;
  isExchangeOrder: boolean;
  onUpdated: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [nextSettlementDate, setNextSettlementDate] = useState(
    formatDateInputValue(settlementDate),
  );
  const [nextStatus, setNextStatus] = useState(status || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const normalizedStatus = status || "";
  const statusDate = formatShortDate(settlementDate);
  const label = getOrderStatusLabel(normalizedStatus);
  const showStatusPillOnly = ["Canceled", "Duplicate"].includes(normalizedStatus);
  const showStatusPillWithDate =
    ["Hold", "Transferred"].includes(normalizedStatus) ||
    (isExchangeOrder && ["Closed", "Completed"].includes(normalizedStatus));

  useEffect(() => {
    setNextSettlementDate(formatDateInputValue(settlementDate));
    setNextStatus(status || "");
  }, [settlementDate, status]);

  const handleClose = () => {
    if (!isSubmitting) {
      setErrorMessage("");
      setOpen(false);
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await axios.post("/api/orderdocuments/UpdateClosingInformation", {
        orderNumber,
        settlementDate: nextSettlementDate
          ? new Date(`${nextSettlementDate}T00:00:00`).toISOString()
          : null,
        status: nextStatus,
      });
      setOpen(false);
      await onUpdated();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(
          typeof error.response?.data === "string"
            ? error.response.data
            : error.response?.data?.message ||
                "Something went wrong while saving. Please try again.",
        );
      } else {
        setErrorMessage("Something went wrong while saving. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showStatusPillOnly) {
    return <OrderStatusPill status={normalizedStatus} />;
  }

  return (
    <>
      <Box sx={{ minWidth: 0, position: "relative" }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: 11,
              fontWeight: 400,
              lineHeight: 1.2,
              mb: 0.5,
            }}
          >
            {label}
          </Typography>
          <Tooltip title="Edit Closing Date">
            <IconButton
              size="small"
              onClick={() => {
                setErrorMessage("");
                setOpen(true);
              }}
              sx={{
                width: 28,
                height: 28,
                mt: -0.75,
                color: "primary.dark",
                backgroundColor: "rgba(100, 105, 115, 0.12)",
                "&:hover": {
                  backgroundColor: "rgba(100, 105, 115, 0.28)",
                },
              }}
            >
              <Iconify icon="eva:edit-2-outline" width={20} />
            </IconButton>
          </Tooltip>
        </Stack>
        <ClickToCopy text={statusDate}>
          <Typography
            variant="body1"
            sx={{
              color: getOrderStatusColor(normalizedStatus),
              fontSize: 16,
              fontWeight: 400,
              lineHeight: 1.15,
              wordBreak: "break-word",
            }}
          >
            {statusDate}
          </Typography>
        </ClickToCopy>
      </Box>

      {showStatusPillWithDate && <OrderStatusPill status={normalizedStatus} />}

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "primary.dark" }}>
          Edit Closing Info
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField size="small"
              label="Settlement Date"
              type="date"
              value={nextSettlementDate}
              onChange={(event) => setNextSettlementDate(event.target.value)}
              disabled={isSubmitting}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField size="small"
              select
              fullWidth
              label="Status"
              value={nextStatus}
              onChange={(event) => setNextStatus(event.target.value)}
              disabled={isSubmitting}
              sx={{
                "& .MuiSelect-select": {
                  color: statusColorMap[nextStatus] || statusColorMap.Default,
                  fontWeight: 700,
                },
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  sx={{
                    color: statusColorMap[option] || statusColorMap.Default,
                    fontWeight: 600,
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </TextField>

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button size="small"
            variant="text"
            color="inherit"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function OrderStatusPill({ status }: { status: string }) {
  const isCanceled = status.toLowerCase().includes("cancel");

  return (
    <Box sx={{ alignSelf: "center", minWidth: 0 }}>
      <Chip
        label={status.toUpperCase()}
        sx={{
          width: "100%",
          borderRadius: 1,
          fontSize: 12,
          height: 28,
          fontWeight: 500,
          color: isCanceled ? "error.dark" : "#ac0000",
          backgroundColor: (theme) =>
            alpha(
              isCanceled
                ? theme.palette.error.main
                : theme.palette.warning.main,
              0.12,
            ),
        }}
      />
    </Box>
  );
}

function getOrderStatusLabel(status: string) {
  switch (status) {
    case "Closed":
    case "Completed":
      return `${status} on`;
    case "In process":
      return "Est. closing date";
    case "Firm":
      return "Scheduled to close";
    case "Pending":
      return "Pending";
    case "Adjourned":
      return "Adjourned from";
    case "Tentative":
      return "Tentative Closing date";
    case "Hold":
    case "Transferred":
      return "Settlement Date";
    default:
      return "Settlement date";
  }
}

function getOrderStatusColor(status: string) {
  switch (status) {
    case "Completed":
      return "#3b49d4";
    case "Closed":
      return "#1a5417";
    case "Canceled":
    case "Duplicate":
      return "#ff2929";
    case "Firm":
      return "#47a842";
    case "Pending":
      return "#552899";
    case "Adjourned":
      return "#e0a800";
    default:
      return "#000e48";
  }
}

function PropertyDetails({ property }: { property: DocumentsOrderProperty }) {
  const hasBlockAndLots = Boolean(property.block && property.lots?.[0]);
  const details: {
    label: string;
    value?: string;
    isMultiline?: boolean;
  }[] = [
    { label: "Section", value: property.section },
    ...(hasBlockAndLots
      ? [
          { label: "Block", value: property.block },
          {
            label: property.lots && property.lots.length > 1 ? "Lots" : "Lot",
            value: property.lots?.join(" & "),
          },
        ]
      : [
          {
            label: "Parcel Id",
            value: property.parcelIds?.join("\n"),
            isMultiline: true,
          },
        ]),
    { label: "County", value: property.county },
    {
      label: "Township",
      value:
        property.city !== property.township ? property.township : undefined,
    },
  ].filter((item) => item.value);

  if (details.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
        columnGap: 2,
        rowGap: 1,
      }}
    >
      {details.map((detail) => (
        <Box key={detail.label} sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: 11,
              fontWeight: 400,
              lineHeight: 1.2,
              mb: 0.25,
            }}
          >
            {detail.label}
          </Typography>
          <ClickToCopy text={String(detail.value)}>
            <Typography
              variant="body1"
              sx={{
                color: "primary.dark",
                fontSize: 14,
                fontWeight: 400,
                lineHeight: 1.15,
                wordBreak: "break-word",
                whiteSpace: detail.isMultiline ? "pre-line" : "normal",
              }}
            >
              {detail.value}
            </Typography>
          </ClickToCopy>
        </Box>
      ))}
    </Box>
  );
}
